<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useAuthorStore } from "@/stores/useAuthorStore";
import CustomTextarea from "../Generics/CustomTextarea.vue";
import ConfirmAuthorDuplicateModal from "./ConfirmAuthorDuplicateModal.vue";
import AuthorCardModal from "./AuthorCardModal.vue";
import { toTitleCase } from "@/utils/stringUtils";

import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "author-added"]);
const authorStore = useAuthorStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const props = defineProps({
  initialName: { type: String, default: "" },
});

const obj = ref({ authorId: 0, authorName: "", description: "" });
const nameInput = ref(null);
const isSubmitting = ref(false);

// confirmação (409)
const showConfirmModal = ref(false);
const confirmMessage = ref("");
const existingAuthors = ref([]);
const pendingAction = ref(null); // "create"

// card modal (opcional)
const showCardModal = ref(false);
const selectedAuthorId = ref(null);

// TOAST (mantém seu padrão)
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

// Ao criar com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("close") desmontar o componente.
const hideBackdropForSuccess = ref(false);

// ✅ PATCH: ref do wrapper que contém o textarea real
const descriptionWrapRef = ref(null);


// ⬇️ NOVO: flag para saber se o usuário já confirmou a duplicidade
const allowDuplicateOnNextSubmit = ref(false);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}
function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

watch(
  () => props.initialName,
  (newName) => {
    obj.value.authorName = newName || "";
  },
  { immediate: true }
);

function closeModal() {
  if (isSubmitting.value) return;
  emit("close");
}

function normalizeName() {
  obj.value.authorName = toTitleCase(
    (obj.value.authorName ?? "").toString().trim().replace(/\s+/g, " ")
  );
}

// ✅ PATCH: foco robusto no textarea/contenteditable interno do CustomTextarea
function focusDescriptionField() {
  // precisamos esperar o DOM assentar após fechar o modal de confirmação
  nextTick(() => {
    // 2 frames costuma resolver transições/render tardio de componentes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const wrap = descriptionWrapRef.value;
        if (!wrap) return;

        // tenta textarea, depois contenteditable (caso seu CustomTextarea use div editável)
        const el =
          wrap.querySelector("textarea") ||
          wrap.querySelector('[contenteditable="true"]') ||
          wrap.querySelector("input");

        if (el && typeof el.focus === "function") {
          el.focus();
          // opcional: seleciona tudo se for textarea/input
          if (typeof el.select === "function") el.select();
        }
      });
    });
  });
}


function maybeOpenConfirmFromError(error) {
  const data = error?.response?.data;
  if (error?.response?.status === 409 && data?.requiresConfirmation) {
    confirmMessage.value = data.message || "Confirme para prosseguir.";
    existingAuthors.value = Array.isArray(data.existingAuthors)
      ? data.existingAuthors
      : [];
    pendingAction.value = "create";
    showConfirmModal.value = true;
    return true;
  }
  return false;
}

// ⬇️ ATUALIZADO: agora usa allowDuplicateOnNextSubmit em vez de parâmetro
async function cadastrar() {
  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;
    normalizeName();

    // usamos a flag: se o usuário já confirmou duplicidade,
    // mandamos allowDuplicateName = true
    const allowDuplicateName = allowDuplicateOnNextSubmit.value === true;

    const createdAuthor = await authorStore.createAuthor(
      obj.value,
      allowDuplicateName
    );

    showSuccessToast("Autor criado com sucesso.");
    hideBackdropForSuccess.value = true;

    setTimeout(() => {
      isSubmitting.value = false;
      emit("author-added", {
        label: createdAuthor.authorName,
        value: createdAuthor.authorId,
      });
      emit("close");
    }, 2400);
  } catch (error) {
    if (maybeOpenConfirmFromError(error)) {
      // 409 → abriu modal de confirmação, não fecha o formulário
      isSubmitting.value = false;
      return;
    }

    let userFriendlyMessage =
      "Ocorreu um erro desconhecido ao cadastrar o autor.";
    if (error.response?.data?.errors) {
      const validationErrors = error.response.data.errors;
      const messages = [];
      for (const field in validationErrors) {
        if (validationErrors[field]?.length)
          messages.push(validationErrors[field][0]);
      }
      if (messages.length) userFriendlyMessage = messages.join("\n");
    } else if (error.response?.data?.message) {
      userFriendlyMessage = error.response.data.message;
    } else if (error.message) {
      userFriendlyMessage = error.message;
    }

    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

// ⬇️ ATUALIZADO: não cria de imediato; apenas marca flag e foca descrição
async function confirmProceed() {
  // usuário decidiu prosseguir → marcamos que a próxima submissão
  // deve enviar allowDuplicateName = true
  allowDuplicateOnNextSubmit.value = true;
  showConfirmModal.value = false;

  // foca no campo de descrição para o usuário complementar os dados
  focusDescriptionField();
}

function handleViewAuthor(authorId) {
  selectedAuthorId.value = authorId;
  showCardModal.value = true;
}

const handleEscKey = (event) => {
  if (
    event.key === "Escape" &&
    !isSubmitting.value &&
    !showConfirmModal.value
  ) {
    emit("close");
  }
};

onMounted(() => {
  nextTick(() => nameInput.value?.focus());
  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div v-show="!hideBackdropForSuccess" class="modal-backdrop">
    <div ref="panelRef" class="modal-panel small" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Adicionar Novo Autor</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <form @submit.prevent="cadastrar(false)" novalidate>
        <div class="modal-body">
          <div class="form-group">
            <label for="name" class="form-label">Nome</label>
            <input
              ref="nameInput"
              type="text"
              id="name"
              v-model="obj.authorName"
              @blur="obj.authorName = toTitleCase(obj.authorName)"
              class="input-base"
              placeholder="Digite o nome do autor"
              required
              @focus="$event.target.select()"
            />
          </div>

          <div class="form-group">
            <label for="description" class="form-label"
              >Descrição (opcional)</label
            >
            <!-- ✅ PATCH: wrapper com ref para conseguir focar no elemento real -->
            <div ref="descriptionWrapRef">
              <CustomTextarea
                v-model:body="obj.description"
                placeholder="Digite uma breve descrição (opcional)"
                :is-disabled="false"
                style="min-height: 120px"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>

            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>

            <span>{{
              isSubmitting ? "Adicionando..." : "Adicionar Autor"
            }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <ConfirmAuthorDuplicateModal
      :show="showConfirmModal"
      :title="'Autor já existe: ' + toTitleCase(obj.authorName)"
      :message="confirmMessage"
      :existing-authors="existingAuthors"
      :is-submitting="isSubmitting"
      @close="showConfirmModal = false"
      @confirm="confirmProceed"
      @view-author="handleViewAuthor"
    />

    <AuthorCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :author-id="selectedAuthorId"
      @close="showCardModal = false"
    />
  </Teleport>

  <!-- mantém seu toast igual -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[10050] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>



